export const MIN_WORD_HOLD_MS = 180;
export const MIN_PHRASE_WORDS = 3;
export const MAX_PHRASE_WORDS = 5;

export function normalizeWordEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const text = String(entry.text ?? entry.word ?? "").trim();
  if (!text) return null;
  const startMs = Number(entry.startMs ?? entry.start ?? NaN);
  const endMs = Number(entry.endMs ?? entry.end ?? NaN);
  if (!Number.isFinite(startMs)) return null;
  return {
    text,
    startMs,
    endMs: Number.isFinite(endMs) ? endMs : startMs + MIN_WORD_HOLD_MS,
  };
}

export function normalizeCaptionWords(captionWords) {
  if (!Array.isArray(captionWords)) return [];
  return captionWords.map(normalizeWordEntry).filter(Boolean);
}

export function extendWordTimings(words) {
  return words.map((word) => ({
    ...word,
    endMs: Math.max(word.endMs, word.startMs + MIN_WORD_HOLD_MS),
  }));
}

/**
 * Active word with min hold and gap freeze — highlight stays until the next word starts.
 */
export function resolveActiveWordIndex(words, currentMs) {
  if (!words.length) return -1;

  const extended = extendWordTimings(words);

  for (let i = extended.length - 1; i >= 0; i -= 1) {
    const word = extended[i];
    const nextStart = i + 1 < extended.length ? extended[i + 1].startMs : Infinity;
    if (currentMs >= word.startMs && currentMs < nextStart) {
      return i;
    }
  }

  if (currentMs >= extended[extended.length - 1].startMs) {
    return extended.length - 1;
  }

  return -1;
}

/**
 * Sticky phrase window — shifts only when the active word leaves center positions 1–2.
 */
export function computeStickyPhraseWindow(words, activeIndex) {
  if (!words.length) return [];

  if (activeIndex < 0) {
    return words
      .slice(0, Math.min(MAX_PHRASE_WORDS, words.length))
      .map((word, index) => ({ word, globalIndex: index }));
  }

  let windowStart = 0;
  for (let i = 0; i <= activeIndex; i += 1) {
    const localIdx = i - windowStart;
    if (localIdx > 2) {
      windowStart = i - 2;
    } else if (localIdx < 1 && windowStart > 0) {
      windowStart = Math.max(0, i - 1);
    }
  }

  let end = Math.min(words.length, windowStart + MAX_PHRASE_WORDS);
  if (end - windowStart < MIN_PHRASE_WORDS) {
    windowStart = Math.max(0, end - MIN_PHRASE_WORDS);
    end = Math.min(words.length, windowStart + MAX_PHRASE_WORDS);
  }

  return words.slice(windowStart, end).map((word, offset) => ({
    word,
    globalIndex: windowStart + offset,
  }));
}

export function resolveSceneForFrame(scenes, frame) {
  if (!Array.isArray(scenes) || !scenes.length) return null;

  const matched = scenes.find((scene) => {
    const start = Number(scene.startFrame);
    const end = Number(scene.endFrame);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return frame >= start && frame < end;
  });

  return matched || null;
}

/** Scene-scoped caption words only — no global wordAlignment fallback. */
export function resolveSceneCaptionWords(scenes, frame) {
  const scene = resolveSceneForFrame(scenes, frame);
  if (!scene?.captionWords?.length) return [];
  return normalizeCaptionWords(scene.captionWords);
}
